import { getSafe } from "@jslib/shared/utils/getSafe";

import Message from "@components/Message";
import { PoweredByComponent } from "@components/PoweredBy/PoweredByComponent";
import { LogoComponent } from "@components/LogoComponent/LogoComponent";
import { LinkExternal } from "@components/LinkExternal";
import { TryAgainActionComponent } from "@components/TryAgainAction";
import { AnnouncingHeader } from "@components/AnnouncingHeader/AnnouncingHeader";

import {
  type VerificationService,
  type ErrorId,
  type ErrorResponse,
  type ErrorDetailId,
} from "@/lib/types/types";
import { VerificationStepsEnum } from "@/lib/types/runtimeTypes";
import { getOption } from "@/options/options";
import { recordEvent } from "@/lib/GoogleAnalytics/ga";
import { type MessageId } from "@/lib/intl/types";

interface StepErrorProps {
  verificationService: VerificationService;
  errorId?: ErrorId;
}

const ErrorTitle = ({
  verificationService,
  errorIds,
}: {
  verificationService: VerificationService;
  errorIds: ErrorId[];
}) => {
  const firstErrorId = errorIds[0];
  const errorMessageId: MessageId = verificationService?.messages[
    `step.error.errorId.${firstErrorId}.title`
  ]
    ? `step.error.errorId.${firstErrorId}.title`
    : "step.error.title";

  return (
    <AnnouncingHeader align="center">
      <h1>
        <Message id={errorMessageId} />
      </h1>
    </AnnouncingHeader>
  );
};

const ErrorMsg = ({
  errorIds,
  errorDetailId,
}: {
  errorIds: ErrorId[];
  errorDetailId: ErrorDetailId;
}) => {
  const displayErrorMessageIds = new Map();
  if (errorDetailId) {
    displayErrorMessageIds.set(errorDetailId, `errorDetailId.${errorDetailId}`);
  } else {
    errorIds?.forEach((errId) => displayErrorMessageIds.set(errId, `errorId.${errId}`));
  }

  if (displayErrorMessageIds.size > 0) {
    return (
      <p>
        {Array.from(displayErrorMessageIds).map(([errId, errMsgId]) => (
          <Message key={errMsgId} id={errMsgId} />
        ))}
      </p>
    );
  }

  return null;
};

const ErrorButton = ({
  verificationService,
  errorIds,
  redirectUrl,
}: {
  verificationService: VerificationService;
  errorIds: ErrorId[];
  redirectUrl?: string;
}) => {
  if (errorIds.includes("unauthorizedAccountStatus")) {
    const faqUrl = getSafe(() => verificationService.programTheme.config.brandInfo.faqUrl);
    if (!faqUrl) return null;
    return (
      <LinkExternal
        href={faqUrl}
        onClick={() => recordEvent(VerificationStepsEnum.error, faqUrl)}
        className="sid-btn sid-faq-btn sid-btn-light"
      >
        <Message id="step.error.errorId.unauthorizedAccountStatus.buttonText" />
      </LinkExternal>
    );
  }

  if (errorIds.includes("verificationLimitExceeded")) return null;

  if (
    errorIds.includes("missingRequiredMetadata") ||
    errorIds.includes("missingRequiredExternalId")
  ) {
    if (getOption("tryAgainAction").type === "reset") return null;
    return <TryAgainActionComponent />;
  }

  const firstErrorId = errorIds[0];
  if (
    verificationService.messages?.[`step.error.errorId.${firstErrorId}.buttonText`] &&
    redirectUrl
  ) {
    return (
      <LinkExternal
        href={redirectUrl}
        onClick={() => recordEvent(VerificationStepsEnum.error, redirectUrl)}
        className="sid-btn sid-error-btn sid-btn-light"
      >
        <Message id={`step.error.errorId.${firstErrorId}.buttonText`} />
      </LinkExternal>
    );
  }

  return <TryAgainActionComponent />;
};

export const StepErrorComponent = ({ verificationService, errorId }: StepErrorProps) => {
  const verificationResponse = verificationService.verificationResponse as ErrorResponse;
  const { errorIds: responseErrorIds, redirectUrl, errorDetailId } = verificationResponse;

  const errorIdsSet = new Set<ErrorId>();
  if (errorId) errorIdsSet.add(errorId);
  if (Array.isArray(responseErrorIds)) responseErrorIds.forEach((errId) => errorIdsSet.add(errId));
  const errorIds = Array.from(errorIdsSet);

  return (
    <div id="sid-step-error" className="sid-error-container sid-l-container">
      <div className="sid-header sid-l-horz-center" role="banner">
        <div className="sid-error-logo-container sid-l-horz-center">
          <LogoComponent
            verificationService={verificationService}
            fallbackLogo={
              <img
                className="sid-error-logo sid-l-lead-image"
                alt=""
                src="https://assets-resources.sheerid.com/common/images/2018/icons/circle-exclamation.svg"
              />
            }
          />
        </div>
        <ErrorTitle verificationService={verificationService} errorIds={errorIds} />
        <div className="sid-header__subtitle sid-l-horz-center sid-l-space-top-md">
          <div className="sid-error-msg sid-l-horz-center">
            <ErrorMsg errorIds={errorIds} errorDetailId={errorDetailId} />
          </div>
          <div className="sid-l-space-top-lg sid-error-button-container sid-l-horz-center">
            <ErrorButton
              verificationService={verificationService}
              errorIds={errorIds}
              redirectUrl={redirectUrl}
            />
          </div>
        </div>
      </div>

      <div className="sid-footer sid-powered-by-container sid-l-horz-center sid-l-space-top-xl sid-h-small-text">
        <PoweredByComponent verificationService={verificationService} isCentered />
      </div>
    </div>
  );
};
